import PreviewImageFile from "./Previews/PreviewImageFile";
import PreviewAudioFile from "./Previews/PreviewAudioFile";
import PreviewVideoFile from "./Previews/PreviewVideoFile";
import Spinner from "../../../Spinner";
import {useEffect} from "react";
import filterResponse from "../../../../filterResponse";
import PreviewTextFile from "./Previews/PreviewTextFile/PreviewTextFile";
import PreviewArchiveFile from "./Previews/PreviewArchiveFile/PreviewArchiveFile";
import ActionsRow from "./ActionsRow";
import ReviewStatusCell from "./ReviewStatusCell";

function renderInputValue(input, entry, highlight) {
	if (input.dropDown) {
		if (input.multiple) {
			return entry.answers.dropDownsMultiple.find(value => value.id === input.id).value.toString();
		}
		return entry.answers.dropDowns.find(value => value.id === input.id).value.toString();
	}
	if (input.boolean) {
		return <input type={"checkbox"} checked={entry.answers.booleans.find(value => value.id === input.id).checked} readOnly={true}/>;
	}
	if (input.textField) {
		return entry.answers.textFields.find(value => value.id === input.id).value;
	}
	if (input.regexField) {
		return entry.answers.regexFields.find(value => value.id === input.id).value;
	}
	if (input.file) {
		const file = entry.answers.files.find(value => value.id === input.id);
		if (input.type === "image") {
			return <PreviewImageFile file={file} highlight={highlight} />;
		}
		if (input.type === "audio") {
			return <PreviewAudioFile file={file} highlight={highlight} />;
		}
		if (input.type === "video") {
			return <PreviewVideoFile file={file} highlight={highlight} />;
		}
		if (input.type === "text") {
			return <PreviewTextFile file={file} highlight={highlight} />;
		}
		if (input.type === "archive") {
			return <PreviewArchiveFile file={file} highlight={highlight} />;
		}
		return null;
	}
	if (input.list) {
		const list = entry.answers.lists.find(value => value.id === input.id);
		return list.value.map(value => value.value).join(list.delimiter);
	}
	return null;
}

function QaStatus({entry}) {
	if (!entry.qaDone) {
		return <div className={"text-center"}><Spinner small={true} /></div>;
	}
	return (
		<ul className={"m-0 ps-3"}>
			{entry.qaErrors?.length > 0 &&
				entry.qaErrors.map((value, index) => {
					return <li key={index}>{value}</li>
				})
			}
		</ul>
	);
}

export default function EntryRow({inputs, entry, job, deleteEntry, editEntry, page, quantity, index, inspector, highlight, layout = "table"}) {
	useEffect(() => {
		let interval = null;
		let isMounted = true;

		if (!entry.qaDone) {
			interval = setInterval(async () => {
				try {
					const response = await fetch(process.env.REACT_APP_ENDPOINT + '/crowdEntries/' + entry.id, {
						method: "GET",
						credentials: "include"
					});
					await filterResponse(response);

					if (response.status === 200) {
						const {entry} = await response.json();
						isMounted && await editEntry(entry.id, entry);
					}
					else {
						throw new Error("Wrong status code.");
					}
				}
				catch (e) {
					console.error(e);
				}
			}, 5000);
		}

		return () => {
			isMounted = false;
			interval && clearInterval(interval);
		};
	}, [entry.qaDone, entry.id]);

	function splitIntoLines(text, lines = 2) {
		const words = text.split(/\s+/).filter(Boolean);
		if (!words.length) return Array(lines).fill('');
		// target words per line (float)
		const target = words.length / lines;
		const result = [];
		let i = 0;
		for (let line = 0; line < lines; line++) {
			const start = Math.round(line * target);
			const end = Math.round((line + 1) * target);
			result.push(words.slice(start, end).join(' '));
			i = end;
		}
		// if some words remain, append to last line
		if (i < words.length) result[result.length - 1] += (result[result.length - 1] ? ' ' : '') + words.slice(i).join(' ');
		return result;
	}

	function RenderWithBreaks( text, lines = 2 ) {
		const parts = splitIntoLines(text, lines);
		return (
			<>
				{parts.map((p, idx) => (
					<span key={idx}>
          {p}
						{idx < parts.length - 1 && <br />}
        </span>
				))}
			</>
		);
	}

	const hasQaErrors = entry.qaErrors?.length > 0;

	if (layout === "card") {
		return (
			<div className={"card mb-2" + (hasQaErrors ? (entry.qaBlocked ? " border-danger" : " border-warning") : "")}>
				<div className={"card-header d-flex justify-content-between align-items-center gap-2 py-2 px-3" + (hasQaErrors ? (entry.qaBlocked ? " bg-danger-subtle" : " bg-warning-subtle") : "")}>
					<span className={"fw-semibold text-nowrap"}>#{entry.id}</span>
					<ActionsRow entry={entry} job={job} deleteEntry={deleteEntry} editEntry={editEntry} inspector={inspector} asCell={false} />
				</div>
				<div className={"card-body py-2 px-3"}>
					{inputs.map(input => (
						<div key={input.id} className={"py-2 border-bottom"}>
							<div className={"fw-semibold text-secondary small mb-1"}>{input.name}</div>
							<div className={"text-break"}>{renderInputValue(input, entry, highlight)}</div>
						</div>
					))}
					<div className={"py-2 border-bottom"}>
						<div className={"fw-semibold text-secondary small mb-1"}>Stamp</div>
						<div className={"text-break"}>{entry.stamp}</div>
					</div>
					<div className={"py-2"}>
						<div className={"fw-semibold text-secondary small mb-1"}>QA errors</div>
						<QaStatus entry={entry} />
					</div>
				</div>
			</div>
		);
	}

	return (
		<tr className={hasQaErrors ? (entry.qaBlocked ? "table-danger" : "table-warning"): ""}>
			<td className={"position-sticky"} style={{left:0}}>{entry.id}</td>
			{inputs.map(input => {
				return <td key={input.id} className={"overflow-hidden text-break"}>{renderInputValue(input, entry, highlight)}</td>
			})}
			<td className={"text-nowrap"}>{entry.stamp}</td>
			<td>
				<QaStatus entry={entry} />
			</td>
			<ReviewStatusCell entry={entry} editEntry={editEntry} inspector={inspector} />
			<ActionsRow entry={entry} job={job} deleteEntry={deleteEntry} editEntry={editEntry} inspector={inspector} />
		</tr>
	);
}
